import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { CalendarCheck, History, MessageCircle, X } from "lucide-react";
import { Conversation, ConversationContent, ConversationScrollButton } from "@/components/ai-elements/conversation";
import { Message, MessageContent, MessageResponse } from "@/components/ai-elements/message";
import { PromptInput, PromptInputFooter, PromptInputSubmit, PromptInputTextarea } from "@/components/ai-elements/prompt-input";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { Button } from "@/components/ui/button";
import { SITE } from "@/lib/site";
import { closeChat, sendChatMessage, startChat } from "@/lib/symptom-chat.functions";

type Msg = { role: "user" | "assistant"; content: string; at?: string };
type Session = { id: string; token: string; name: string };
type Step = "name" | "phone" | "chat";

const GREETING: Msg = { role: "assistant", content: "Hi there! I'm Physio Bot from N S Physiotherapy. Before we start, what's your name?" };
const COMMON_SERVICES = [
  { label: "Back pain", question: "I need help with back pain." },
  { label: "Neck pain", question: "I need help with neck pain." },
  { label: "Sports injury", question: "I need help with a sports injury." },
  { label: "After surgery", question: "I need physiotherapy after surgery." },
  { label: "Chest physio", question: "I need chest physiotherapy." },
  { label: "Home visit", question: "I would like a physiotherapy home visit." },
] as const;

export function PhysioBotPanel({ onClose, className = "" }: { onClose?: () => void; className?: string }) {
  const start = useServerFn(startChat);
  const send = useServerFn(sendChatMessage);
  const close = useServerFn(closeChat);

  const [step, setStep] = useState<Step>("name");
  const [name, setName] = useState("");
  const [intro, setIntro] = useState<Msg[]>([GREETING]);
  const [session, setSession] = useState<Session | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [closed, setClosed] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const sessionRef = useRef<Session | null>(null);
  sessionRef.current = closed ? null : session;

  const shown = [...intro, ...messages];
  const visible = showHistory ? shown : shown.slice(-1);
  useEffect(() => {
    const onHide = () => { const s = sessionRef.current; if (s) void close({ data: { id: s.id, token: s.token } }); };
    window.addEventListener("pagehide", onHide);
    return () => { window.removeEventListener("pagehide", onHide); onHide(); };
  }, [close]);

  async function onSend(value = input) {
    const text = value.trim();
    if (!text || busy) return;
    setError(null);
    setShowHistory(false);

    if (step === "name") {
      if (text.length < 2) return;
      setInput(""); setName(text);
      setIntro((m) => [...m, { role: "user", content: text }, { role: "assistant", content: `Nice to meet you, ${text.split(" ")[0]}! What's your cell number, in case we need to follow up?` }]);
      setStep("phone");
      return;
    }
    if (step === "phone") {
      setInput("");
      if (!/^[+0-9 ()-]{9,20}$/.test(text)) {
        setIntro((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "Hmm, that doesn't look like a cell number. Could you try again? e.g. 082 123 4567" }]);
        return;
      }
      setIntro((m) => [...m, { role: "user", content: text }]);
      setBusy(true);
      try {
        const r = await start({ data: { name, phone: text } });
        setSession({ id: r.id, token: r.token, name });
        setMessages(r.messages as Msg[]);
        setStep("chat");
      } catch { setError("Could not start the chat. Please try again."); setIntro((m) => m.slice(0, -1)); setInput(text); }
      finally { setBusy(false); }
      return;
    }
    if (!session) return;
    setInput(""); setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }]);
    try {
      const r = await send({ data: { id: session.id, token: session.token, text } });
      if (r.ok) setMessages(r.messages as Msg[]); else { setMessages((m) => m.slice(0, -1)); setInput(text); setError(r.error); }
    } catch { setMessages((m) => m.slice(0, -1)); setInput(text); setError("Sorry, something went wrong. Please try again."); }
    finally { setBusy(false); }
  }

  async function endChat(whatsapp = false) {
    if (!session || closed) return;
    setClosed(true);
    try { await close({ data: { id: session.id, token: session.token, whatsapp } }); } catch { /* safety net emails later */ }
  }

  const waText = `Hi, I'm ${name || "a website visitor"}. I was chatting with Physio Bot and would like to speak to the physio.`;
  const waUrl = `https://wa.me/${SITE.whatsappIntl}?text=${encodeURIComponent(waText)}`;
  const placeholder = step === "name" ? "Type your name..." : step === "phone" ? "Type your cell number..." : "Describe the problem...";

  return (
    <div className={`flex min-h-0 flex-col overflow-hidden rounded-md border border-border bg-background shadow-2xl ${className}`}>
      <div className="grid shrink-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 bg-primary px-3 py-2 text-primary-foreground">
        <div className="flex min-w-0 items-center gap-2 font-display font-semibold"><MessageCircle className="h-5 w-5 shrink-0" /> <span className="truncate">Physio Bot</span></div>
        <div className="flex shrink-0 items-center gap-1">
          {shown.length > 1 && <Button type="button" size="icon" variant="ghost" onClick={() => setShowHistory((v) => !v)} aria-label={showHistory ? "Show latest reply" : "Show earlier messages"} title={showHistory ? "Latest reply" : "Earlier messages"} className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"><History className="h-4 w-4" /></Button>}
          {onClose && <Button type="button" size="icon" variant="ghost" onClick={onClose} aria-label="Close Physio Bot" className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"><X className="h-4 w-4" /></Button>}
        </div>
      </div>
      <Conversation className="min-h-0">
        <ConversationContent className="gap-2 p-3">
          {visible.map((m, i) => (
            <Message key={`${showHistory ? i : shown.length - 1}-${m.content}`} from={m.role} className="max-w-full">
              <MessageContent className="break-words leading-snug group-[.is-user]:bg-primary group-[.is-user]:text-primary-foreground group-[.is-user]:px-3 group-[.is-user]:py-2">
                {renderMessageContent(m.content)}
              </MessageContent>
            </Message>
          ))}
          {!showHistory && shown.length > 1 && <p className="sr-only">Earlier messages are available from the history button.</p>}
          {busy && step === "chat" && <Shimmer className="text-xs">Thinking...</Shimmer>}
          {step === "chat" && !closed && !messages.some((m) => m.role === "user") && (
            <div className="grid grid-cols-3 gap-1.5 pt-1" aria-label="Common physiotherapy services">
              {COMMON_SERVICES.map((service) => (
                <Button key={service.label} type="button" size="sm" variant="outline" disabled={busy} onClick={() => void onSend(service.question)} className="h-8 min-w-0 px-1 text-[11px] leading-tight whitespace-normal text-center">
                  {service.label}
                </Button>
              ))}
            </div>
          )}
        </ConversationContent>
        <ConversationScrollButton aria-label="Scroll to newest message" className="h-8 w-8" />
      </Conversation>
      {error && <p role="alert" className="shrink-0 px-3 pb-1 text-xs text-destructive">{error}</p>}
      {closed ? (
        <div className="shrink-0 border-t border-border p-2 text-sm text-foreground/80">Chat ended. Thank you, {name.split(" ")[0]}!</div>
      ) : (
        <PromptInput onSubmit={({ text }) => void onSend(text)} className="shrink-0 border-t border-border p-2">
          <PromptInputTextarea rows={1} value={input} maxLength={step === "chat" ? 2000 : 80} placeholder={placeholder}
            className="max-h-16 min-h-9 py-2 text-sm" inputMode={step === "phone" ? "tel" : undefined}
            onChange={(e) => setInput(e.target.value)}
          />
          <PromptInputFooter className="justify-end px-2 pb-1 pt-0">
            <PromptInputSubmit status={busy ? "submitted" : error ? "error" : undefined} disabled={busy || !input.trim()} aria-label="Send message" className="h-8 w-8" />
          </PromptInputFooter>
        </PromptInput>
      )}
      {step === "chat" && (
        <div className="flex shrink-0 items-center gap-1 border-t border-border p-1.5">
          <Button asChild size="sm" className="h-8"><Link to="/booking" onClick={() => void endChat()}><CalendarCheck className="h-4 w-4" /> Book</Link></Button>
          <Button asChild size="sm" variant="outline" className="h-8 min-w-0 flex-1 px-2"><a href={waUrl} target="_blank" rel="noreferrer" onClick={() => void endChat(true)}><MessageCircle className="h-4 w-4" /> <span className="truncate">Talk to a human</span></a></Button>
          {!closed && <Button type="button" size="icon" variant="ghost" onClick={() => void endChat()} aria-label="End chat" title="End chat" className="h-8 w-8 shrink-0"><X className="h-4 w-4" /></Button>}
        </div>
      )}
    </div>
  );
}

const URL_SPLIT_RE = /(https?:\/\/[^\s<>"')\]]+)/g;
const URL_TEST_RE = /^https?:\/\//;

function renderMessageContent(content: string) {
  const parts = content.split(URL_SPLIT_RE);
  return parts.map((part, i) =>
    URL_TEST_RE.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noreferrer"
        className="font-medium underline underline-offset-2 break-all hover:opacity-80">
        {part.replace(/^https?:\/\//, "").replace(/\/$/, "")}
      </a>
    ) : (
      <MessageResponse key={i} className="inline whitespace-pre-wrap [&_p]:inline">{part}</MessageResponse>
    )
  );
}

export function PhysioBotLauncher() {
  const [open, setOpen] = useState(false);
  return (
    <>
      {open && (
        <PhysioBotPanel onClose={() => setOpen(false)}
           className="fixed bottom-[7.75rem] right-4 z-50 h-[min(430px,calc(100dvh-9.5rem))] w-[min(320px,calc(100vw-2rem))]" />
      )}
       <div className="fixed bottom-[4.5rem] right-5 z-40">
         <Button type="button" onClick={() => setOpen((o) => !o)} aria-label={open ? "Close Physio Bot" : "Open Physio Bot chat bot"} title="Physio Bot chat bot"
           className="inline-flex h-12 w-12 flex-col items-center justify-center gap-0 rounded-full p-0 shadow-lg transition-transform hover:scale-110">
           {open ? <X className="h-5 w-5" /> : (
             <>
               <MessageCircle className="h-4 w-4" />
               <span className="text-[8px] font-bold leading-none tracking-tight">BOT</span>
             </>
           )}
         </Button>
       </div>
    </>
  );
}
